import { 
  Building2, 
  ShoppingBag, 
  Music, 
  Store, 
  Utensils, 
  Sofa, 
  Dumbbell, 
  Wrench, 
  Shirt, 
  Activity,
  ShoppingCart,
  Pill,
  Hammer,
  Laptop,
  Wine,
  Package
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

/**
 * Shared brand definitions for SearchDropdown and LocationComparer2
 * Update this file to add/remove brands - changes will apply to both components
 */

export const BRANDS = [
  'Westfield',
  'Costco',
  'JB Hi Fi',
  'Woolworths',
  'Coles',
  'Aldi',
  'Harris Farm',
  'Kmart',
  'Harvey Norman',
  'Anytime Fitness',
  'UFC Gym',
  'Big W',
  'Supercheap Auto',
  'ZARA',
  'Myer',
  'David Jones',
  'Rebel',
  'Sushi Hub',
  'Subway',
  'IGA',
  'Supabarn',
  'Dan Murphys',
  'Liquorland',
  'Mitre 10',
  'Bunnings',
  'Repco',
  'Total Tools',
  'Sydney Tools',
  'Target',
  'TK Maxx',
  'Uniqlo',
  'Bing Lee',
  'The Good Guys',
  'Officeworks',
  'Apple',
  'Samsung',
  'Chemist Warehouse',
  'IKEA',
] as const;

export type Brand = typeof BRANDS[number];

/**
 * Brand icons mapping
 * Add new icons here when adding brands
 */
export const BRAND_ICONS: Record<Brand, LucideIcon> = {
  'Westfield': Building2,
  'Costco': ShoppingBag,
  'JB Hi Fi': Music,
  'Woolworths': Store,
  'Coles': Store,
  'Aldi': Store,
  'Harris Farm': Utensils,
  'Kmart': Store,
  'Harvey Norman': Sofa,
  'Anytime Fitness': Dumbbell,
  'UFC Gym': Dumbbell,
  'Big W': Store,
  'Supercheap Auto': Wrench,
  'ZARA': Shirt,
  'Myer': ShoppingBag,
  'David Jones': ShoppingBag,
  'Rebel': Activity,
  'Sushi Hub': Utensils,
  'Subway': Utensils,
  'IGA': ShoppingCart,
  'Supabarn': ShoppingCart,
  'Dan Murphys': Wine,
  'Liquorland': Wine,
  'Mitre 10': Hammer,
  'Bunnings': Hammer,
  'Repco': Wrench,
  'Total Tools': Wrench,
  'Sydney Tools': Wrench,
  'Target': ShoppingBag,
  'TK Maxx': ShoppingBag,
  'Uniqlo': Shirt,
  'Bing Lee': Music,
  'The Good Guys': Music,
  'Officeworks': Package,
  'Apple': Laptop,
  'Samsung': Laptop,
  'Chemist Warehouse': Pill,
  'IKEA': Sofa,
};

/**
 * Brands that should only return location results (not shops)
 */
export const LOCATION_ONLY_BRANDS: readonly Brand[] = ['Westfield'] as const;

/**
 * Supermarket brands category
 */
export const SUPERMARKET_BRANDS: readonly Brand[] = ['Coles', 'Woolworths', 'Aldi', 'Harris Farm', 'IGA', 'Supabarn'] as const;

/**
 * Electronics brands category
 */
export const ELECTRONICS_BRANDS: readonly Brand[] = ['JB Hi Fi', 'Harvey Norman', 'Bing Lee', 'The Good Guys', 'Apple', 'Samsung'] as const;

/**
 * Helper function to normalize brand names for search
 * Handles special cases like "JB Hi Fi" -> "jb hi-fi"
 */
export const normalizeBrandName = (brand: Brand): string => {
  if (brand === 'JB Hi Fi') {
    return 'jb hi-fi';
  }
  if (brand === 'UFC Gym') {
    return 'ufc gym';
  }
  if (brand === 'Anytime Fitness') {
    return 'anytime fitness';
  }
  if (brand === 'Supercheap Auto') {
    return 'supercheap auto';
  }
  if (brand === 'Dan Murphys') {
    return 'dan murphy';
  }
  if (brand === 'Bunnings') {
    return 'bunnings';
  }
  if (brand === 'The Good Guys') {
    return 'the good guys';
  }
  if (brand === 'Chemist Warehouse') {
    return 'chemist warehouse';
  }
  return brand.toLowerCase();
};

/**
 * Check if a location/shop name matches a brand
 */
export const matchesBrand = (name: string, brand: Brand): boolean => {
  const nameLower = name.toLowerCase();
  const brandNormalized = normalizeBrandName(brand);
  return nameLower.includes(brandNormalized);
};

